import { Injectable } from '@angular/core';
import { MembersService, PushService } from '@careapp-group/ng-sdk-v2-new';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { FrontendApi, RecoveryFlow, RecoveryFlowState, RegistrationFlow } from '@ory/client';
import { from, of as observableOf } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { CAAction } from '../actions';
import * as actions from '../actions/index';
import { State } from '../reducers';
import { auth as authSelectors } from './auth.selectors';

export interface KratosError {
  error: {
    id: string;
    code: number;
    status: string;
    reason: string;
    message: string;
  };
  redirect_browser_to?: string;
}

@Injectable()
export class KratosAuthEffects {
  @Effect()
  doLogin = this.actions$.pipe(
    ofType(actions.RequestLoginAction),
    switchMap((action) =>
      from(
        this.kratos.createBrowserLoginFlow().then((flow) =>
          this.kratos.updateLoginFlow({
            flow: flow.data.id,
            updateLoginFlowBody: {
              identifier: action.email ?? action.phoneNumber,
              password: action.password,
              method: 'password',
              csrf_token: flow.data.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                .attributes['value'],
            },
          })
        )
      ).pipe(
        map((res) =>
          actions.ResponseSuccessAuthLoginAction({
            session: res.data.session,
            userId: res.data.session.identity.metadata_public['ca_member_id'],
          })
        ),
        catchError((err) =>
          observableOf(
            new actions.ResponseFailAuthLoginAction({
              status: err.response.status,
            })
          )
        )
      )
    )
  );

  @Effect({ dispatch: false })
  redirectAfterReset = this.actions$.pipe(
    ofType(actions.RESPONSE_SUCCESS_AUTH_FORGOT_PASSWORD),
    map(() => actions.NavigateAction({ path: ['/', 'auth'] }))
  );

  @Effect()
  doLogout = this.actions$.pipe(
    ofType(actions.RequestAuthLogoutAction),
    switchMap((action) => this.pushService.deleteDevices().pipe(map((response) => action))),
    switchMap((action) =>
      from(
        this.kratos.createBrowserLogoutFlow().then((flow) =>
          this.kratos.updateLogoutFlow({
            token: flow.data.logout_token,
          })
        )
      ).pipe(
        map((res) =>
          actions.ResponseSuccessAuthLogoutAction({ stayOnCurrentRoute: action.stayOnCurrentRoute })
        ),
        catchError((err) =>
          observableOf(
            actions.ResponseSuccessAuthLogoutAction({
              stayOnCurrentRoute: action.stayOnCurrentRoute,
            })
          )
        )
      )
    )
  );

  @Effect()
  clearOnLogout = this.actions$.pipe(
    ofType(actions.ResponseSuccessAuthLogoutAction),
    map((action) => new actions.RequestStateReset())
  );

  @Effect()
  forceLogout = this.actions$.pipe(
    ofType(actions.FORCE_LOGOUT_401),
    map((action) => actions.RequestAuthLogoutAction({}))
  );

  @Effect()
  setMemberMeta = this.actions$.pipe(
    ofType(actions.RequestUpdateMemberMetaAction),
    withLatestFrom(this.store.select(authSelectors.getLoggedInUser)),
    filter(([action, user]) => !!user),
    switchMap(([action, user]) => {
      return this.membersService
        .updateMetadata({ id: user.id, updateMemberMetaDto: action.payload })
        .pipe(
          map((meta) => actions.ResponseSuccessUpdateMemberMetaAction({ payload: meta })),
          catchError((err) =>
            observableOf(actions.ResponseFailUpdateMemberMetaAction({ payload: err }))
          )
        );
    })
  );

  changePassword = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestChangePasswordAction),
      withLatestFrom(this.store.select(authSelectors.getPasswordChangeFlowId)),
      switchMap(([action, flowId]) =>
        from(
          flowId
            ? this.kratos
                .getSettingsFlow({
                  id: flowId,
                })
                .then((flow) =>
                  this.kratos.updateSettingsFlow({
                    flow: flow.data.id,
                    updateSettingsFlowBody: {
                      csrf_token: flow.data.ui.nodes.find(
                        (n) => n.attributes['name'] === 'csrf_token'
                      ).attributes['value'],
                      method: 'password',
                      password: action.password,
                    },
                  })
                )
            : this.kratos.createBrowserSettingsFlow().then((flow) =>
                this.kratos.updateSettingsFlow({
                  flow: flow.data.id,
                  updateSettingsFlowBody: {
                    csrf_token: flow.data.ui.nodes.find(
                      (n) => n.attributes['name'] === 'csrf_token'
                    ).attributes['value'],
                    method: 'password',
                    password: action.password,
                  },
                })
              )
        ).pipe(
          map((res) => {
            return actions.ResponseSuccessChangePasswordAction({
              payload: res.data,
            });
          }),
          catchError((err) => {
            const error = err.response.data.ui.nodes.filter(
              (n) => n.group === 'password' && n.messages?.length > 0
            )[0].messages[0].text;
            return observableOf(
              actions.ResponseFailChangePasswordAction({
                error: error,
              })
            );
          })
        )
      )
    )
  );

  beginRecovery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestBeginAccountRecovery),
      switchMap((action) =>
        from(
          this.kratos.createBrowserRecoveryFlow().then((flow) =>
            this.kratos.updateRecoveryFlow({
              flow: flow.data.id,
              updateRecoveryFlowBody: {
                method: 'code',
                email: action.email,
                csrf_token: flow.data.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                  .attributes['value'],
              },
            })
          )
        ).pipe(
          map((res) => actions.SuccessBeginAccountRecovery({ flow: res.data })),
          catchError((err) => observableOf(actions.FailBeginAccountRecovery({ error: err })))
        )
      )
    )
  );

  completeRecovery$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCompleteAccountRecovery),
      withLatestFrom(this.store.select(authSelectors.getRecoveryFlow)),
      switchMap(([action, flow]) =>
        from(
          this.kratos
            .updateRecoveryFlow({
              flow: flow.id,
              updateRecoveryFlowBody: {
                code: action.code,
                method: 'code',
                csrf_token: flow.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                  .attributes['value'],
              },
            })
            .then((res) => res.data)
            // we need the error payloads, and they get stripped away if we just catch the
            // observable error
            .catch((err) => {
              return err.response.data;
            })
        ).pipe(
          map((res: RecoveryFlow | KratosError) => {
            if ((<KratosError>res)?.error?.id === 'browser_location_change_required') {
              const passwordChangeFlowId = (<KratosError>res).redirect_browser_to?.match(
                /\?flow=([a-z0-9\-]+)/
              )[1];
              return actions.SuccessCompleteAccountRecovery({ res: res, passwordChangeFlowId });
            } else if ((<RecoveryFlow>res).state === RecoveryFlowState.SentEmail) {
              const message = (<RecoveryFlow>res).ui.messages[0].text;
              return actions.FailCompleteAccountRecovery({ error: message });
            }
            return actions.FailCompleteAccountRecovery({ error: res });
          }),
          catchError((res: KratosError) => {
            if (res.error?.id === 'browser_location_change_required') {
              const passwordChangeFlowId =
                res.redirect_browser_to?.match(/\?flow=([a-z0-9\-]+)/)[1];
              return observableOf(
                actions.SuccessCompleteAccountRecovery({ res: res, passwordChangeFlowId })
              );
            }
            return observableOf(actions.FailCompleteAccountRecovery({ error: res }));
          })
        )
      )
    )
  );

  beginRegistrationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestBeginRegistrationFlow),
      withLatestFrom(this.store.select(authSelectors.getPostLoginRoute)),
      switchMap(([action, route]) =>
        from(
          this.kratos.createBrowserRegistrationFlow({
            afterVerificationReturnTo: route,
          })
        ).pipe(
          map((res) =>
            actions.SuccessBeginRegistrationFlow({
              flow: res.data,
            })
          ),
          catchError((err) => observableOf(actions.FailBeginRegistrationFlow({ error: err })))
        )
      )
    )
  );

  completeRegistrationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCompleteRegistrationFlow),
      withLatestFrom(this.store.select(authSelectors.getRegistrationFlow)),
      switchMap(([action, flow]) =>
        from(
          this.kratos
            .updateRegistrationFlow({
              flow: (<RegistrationFlow>flow).id,
              updateRegistrationFlowBody: action.payload,
            })
            // we need the error payloads, and they get stripped away if we just catch the
            // observable error
            .catch((err) => {
              return err.response;
            })
        ).pipe(
          map((res) => {
            if (res.status >= 200 && res.status < 400) {
              return actions.SuccessCompleteRegistrationFlow({ response: res.data });
            } else {
              return actions.FailCompleteRegistrationFlow({ response: res.data });
            }
          }),

          catchError((err) => {
            console.log(err);
            return observableOf(actions.FailCompleteRegistrationFlow({ response: err }));
          })
        )
      )
    )
  );

  createVerificationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestCreateVerificationFlow),
      switchMap((action) =>
        from(this.kratos.createBrowserVerificationFlow()).pipe(
          map((res) => actions.SuccessGetVerificationFlow({ flow: res.data })),
          catchError((err) => observableOf(actions.FailGetVerificationFlow({ error: err })))
        )
      )
    )
  );
  getVerificationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestGetVerificationFlow),
      switchMap((action) =>
        from(this.kratos.getVerificationFlow({ id: action.id })).pipe(
          map((res) => actions.SuccessGetVerificationFlow({ flow: res.data })),
          catchError((err) => observableOf(actions.FailGetVerificationFlow({ error: err })))
        )
      )
    )
  );

  updateVerificationFlow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestUpdateVerificationFlow),
      withLatestFrom(this.store.select(authSelectors.getVerificationFlow)),
      switchMap(([action, flow]) =>
        from(
          this.kratos
            .updateVerificationFlow({
              flow: flow.id,
              updateVerificationFlowBody: {
                method: 'code',
                code: action.code,
                email: action.email,
                csrf_token: flow.ui.nodes.find((n) => n.attributes['name'] === 'csrf_token')
                  .attributes['value'],
              },
            })
            // we need the error payloads, and they get stripped away if we just catch the
            // observable error
            .catch((err) => {
              return err.response;
            })
        ).pipe(
          map((res) => {
            if (res.data.state === 'passed_challenge') {
              return actions.SuccessUpdateVerificationFlow({ response: res.data });
            } else {
              return actions.FailUpdateVerificationFlow({ response: res.data });
            }
          }),

          catchError((err) => {
            console.log(err);
            return observableOf(actions.FailCompleteRegistrationFlow({ response: err }));
          })
        )
      )
    )
  );

  constructor(
    private store: Store<State>,
    private actions$: Actions<CAAction>,
    private membersService: MembersService,
    private pushService: PushService,
    private kratos: FrontendApi
  ) {}
}
